import { useState, useRef, useEffect } from "react";
import type {
  Destination,
  DestinationTypeReference,
} from "../../typings/Destination";
import "./DestinationConfigFields.scss";
import { EditIcon, HelpIcon, CloseIcon } from "../Icons";
import Tooltip from "../Tooltip/Tooltip";
import Button from "../Button/Button";
import { useSidebar } from "../Sidebar/Sidebar";
import { ConfigurationGuide } from "../ConfigurationGuide/ConfigurationGuide";
import { Checkbox } from "../Checkbox/Checkbox";
import KeyValueMapField from "../KeyValueMapField/KeyValueMapField";
import { isCheckedValue } from "../../utils/formHelper";
import CONFIGS from "../../config";

const DestinationConfigFields = ({
  destination,
  type,
  onChange,
}: {
  destination?: Destination;
  type: DestinationTypeReference;
  onChange?: () => void;
}) => {
  const [unlockedSensitiveFields, setUnlockedSensitiveFields] = useState<
    Record<string, boolean>
  >({});
  const [lastUnlockedSensitiveField, setLastUnlockedSensitiveField] = useState<
    string | null
  >(null);
  const sidebar = useSidebar();

  const inputRefs = useRef<Record<string, HTMLInputElement>>({});

  const unlockSensitiveField = (key: string) => {
    setUnlockedSensitiveFields((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setLastUnlockedSensitiveField(key);

    if (inputRefs.current[key]) {
      inputRefs.current[key].value = "";
    }
  };

  const lockSensitiveField = (key: string) => {
    setUnlockedSensitiveFields((prev) => ({
      ...prev,
      [key]: false,
    }));
    setLastUnlockedSensitiveField(null);
    // Restore the original value when locking/canceling
    if (inputRefs.current[key]) {
      inputRefs.current[key].value = destination?.credentials[key] || ""; // Restore original or empty
    }
  };

  useEffect(() => {
    if (
      lastUnlockedSensitiveField &&
      inputRefs.current[lastUnlockedSensitiveField]
    ) {
      inputRefs.current[lastUnlockedSensitiveField].focus();
    }
  }, [lastUnlockedSensitiveField]);

  return (
    <>
      {type.instructions && (
        <Button
          onClick={() =>
            sidebar.toggle("configuration", <ConfigurationGuide type={type} />)
          }
          className="config-guide-button"
        >
          <HelpIcon />
          Configuration Guide
        </Button>
      )}
      {type.setup_link && (
        <div className="destination-setup-link config-guide-button">
          <a
            href={type.setup_link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="button"
          >
            {type.setup_link.cta}
          </a>
        </div>
      )}
      {[...type.config_fields, ...type.credential_fields]
        .filter((field) => {
          // Filter out custom_headers if the feature flag is not enabled
          if (
            field.key === "custom_headers" &&
            CONFIGS.ENABLE_WEBHOOK_CUSTOM_HEADERS !== "true"
          ) {
            return false;
          }
          return true;
        })
        .map((field) => (
          <div key={field.key} className="destination-config-field">
            <label htmlFor={field.key}>
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            {field.type === "text" && (
              <div className="input-container">
                <input
                  ref={(el) => {
                    if (el) inputRefs.current[field.key] = el;
                  }}
                  type={
                    "sensitive" in field && field.sensitive
                      ? "password"
                      : "text"
                  }
                  placeholder={""}
                  id={field.key}
                  name={field.key}
                  defaultValue={
                    "sensitive" in field && field.sensitive
                      ? unlockedSensitiveFields[field.key]
                        ? ""
                        : destination?.credentials[field.key] || field.default
                      : destination?.config[field.key] ||
                        destination?.credentials[field.key] ||
                        field.default
                  }
                  disabled={
                    "sensitive" in field && field.sensitive
                      ? destination?.credentials[field.key] &&
                        !unlockedSensitiveFields[field.key]
                      : field.disabled
                  }
                  required={field.required}
                  minLength={field.minlength}
                  maxLength={field.maxlength}
                  pattern={field.pattern}
                />
                {/* Show Edit button if sensitive and locked */}
                {"sensitive" in field &&
                  field.sensitive &&
                  destination?.credentials[field.key] &&
                  !unlockedSensitiveFields[field.key] && (
                    <button
                      type="button"
                      onClick={() => unlockSensitiveField(field.key)}
                    >
                      <Tooltip content="Edit secret value" align="end">
                        <EditIcon />
                      </Tooltip>
                    </button>
                  )}

                {/* Show Cancel button if sensitive and unlocked */}
                {"sensitive" in field &&
                  field.sensitive &&
                  unlockedSensitiveFields[field.key] && (
                    <button
                      type="button"
                      onClick={() => lockSensitiveField(field.key)}
                    >
                      <Tooltip content="Cancel editing" align="end">
                        <CloseIcon />
                      </Tooltip>
                    </button>
                  )}
              </div>
            )}
            {field.type === "select" && (
              <select
                id={field.key}
                name={field.key}
                defaultValue={
                  destination?.config[field.key] ||
                  destination?.credentials[field.key] ||
                  field.default ||
                  ""
                }
                disabled={field.disabled}
                required={field.required}
                onChange={onChange}
              >
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            {field.type === "checkbox" && (
              <Checkbox
                label=""
                id={field.key}
                name={field.key}
                defaultChecked={
                  (destination?.config[field.key] !== undefined
                    ? isCheckedValue(destination?.config[field.key])
                    : undefined) ??
                  (destination?.credentials[field.key] !== undefined
                    ? isCheckedValue(destination?.credentials[field.key])
                    : undefined) ??
                  (field.default !== undefined
                    ? isCheckedValue(field.default)
                    : undefined) ??
                  false
                }
                disabled={field.disabled}
                required={field.required}
              />
            )}
            {field.description && (
              <p
                className="description"
                style={{
                  marginBottom:
                    field.type === "key_value_map"
                      ? "var(--spacing-2)"
                      : "var(--spacing-4)",
                }}
              >
                {field.description}
              </p>
            )}
            {field.type === "key_value_map" && (
              <KeyValueMapField
                name={field.key}
                defaultValue={
                  destination?.config[field.key] ||
                  destination?.credentials[field.key] ||
                  field.default
                }
                disabled={field.disabled}
                keyPlaceholder={field.key_placeholder}
                valuePlaceholder={field.value_placeholder}
                onChange={onChange}
              />
            )}
          </div>
        ))}
    </>
  );
};

export default DestinationConfigFields;
